import type { Team } from '@cs-shot/battle-sim';
import type { Scene } from './types';
import { fitText } from './text';
import { drawSpriteBox } from './drawSprite';

export const TEAM_COLOR: Record<Team, string> = { t: '#f0b24a', ct: '#5aa0ff' };

export const nameOf = (sc: Scene, id: string) => sc.byId[id]?.name ?? id;
export const teamOf = (id: string): Team => (id.startsWith('ct') ? 'ct' : 't');

export const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

const HP_SEAM = 12;
export const drawHpBar = (sc: Scene, fieldX: number, fieldW: number, safeTop: number) => {
  const { ctx } = sc;
  const sum = (team: Team) =>
    sc.fighters.filter((f) => f.team === team).reduce((a, f) => a + Math.max(0, f.hp), 0);
  const tHp = sum('t');
  const ctHp = sum('ct');
  const total = tHp + ctHp || 1;
  const pad = 16, sidePad = 56, top = safeTop + 12, h = 16;
  const left = fieldX + pad + sidePad, right = fieldX + fieldW - pad - sidePad, barW = right - left;
  const bottom = top + h;
  const seamX = left + barW * (tHp / total);

  ctx.save();

  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  roundRect(ctx, left - 3, top - 3, barW + 6, h + 6, 7); ctx.fill();

  ctx.save();
  roundRect(ctx, left, top, barW, h, 5);
  ctx.clip();

  const tGrad = ctx.createLinearGradient(0, top, 0, bottom);
  tGrad.addColorStop(0, '#f2b659');
  tGrad.addColorStop(1, '#c07d1e');
  ctx.fillStyle = tGrad;
  ctx.beginPath();
  ctx.moveTo(left, top);
  ctx.lineTo(seamX + HP_SEAM / 2, top);
  ctx.lineTo(seamX - HP_SEAM / 2, bottom);
  ctx.lineTo(left, bottom);
  ctx.closePath();
  ctx.fill();

  const cGrad = ctx.createLinearGradient(0, top, 0, bottom);
  cGrad.addColorStop(0, '#5a93f0');
  cGrad.addColorStop(1, '#2057bd');
  ctx.fillStyle = cGrad;
  ctx.beginPath();
  ctx.moveTo(seamX + HP_SEAM / 2, top);
  ctx.lineTo(right, top);
  ctx.lineTo(right, bottom);
  ctx.lineTo(seamX - HP_SEAM / 2, bottom);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.fillRect(left, top + 1, barW, 2);
  ctx.restore();

  ctx.save();
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 8;
  ctx.beginPath();
  ctx.moveTo(seamX + HP_SEAM / 2, top - 2);
  ctx.lineTo(seamX - HP_SEAM / 2, bottom + 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(seamX + HP_SEAM / 2, top - 2);
  ctx.lineTo(seamX - HP_SEAM / 2, bottom + 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(seamX + HP_SEAM / 2, top - 2);
  ctx.lineTo(seamX - HP_SEAM / 2, bottom + 2);
  ctx.stroke();
  ctx.restore();

  const midY = top + h / 2;
  const icon = 30;
  ctx.drawImage(sc.teamIcons.t, left - 10 - icon, midY - icon / 2, icon, icon);
  ctx.drawImage(sc.teamIcons.ct, right + 10, midY - icon / 2, icon, icon);
  ctx.restore();
};

const KF_ICON_H = 20;
const KF_ICON_PAD = 8;
const KF_NAME_MAX = 200;
export const drawKillFeed = (sc: Scene, time: number) => {
  const { ctx, W, killFeed, weaponIcons, headshotIcon } = sc;
  const LIFE = 4.2;
  const rowH = 34;
  let y = 92;
  ctx.save();
  ctx.font = '700 22px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  for (let i = killFeed.length - 1; i >= 0; i--) {
    const k = killFeed[i];
    const age = time - k.t0;
    if (age > LIFE) continue;
    const alpha = Math.min(1, (LIFE - age) / 0.6);
    const icon = weaponIcons[k.byWeapon] ?? weaponIcons.ak;
    const iconW = icon ? icon.bw * (KF_ICON_H / icon.bh) : 0;
    const hsW = k.headshot ? headshotIcon.bw * (KF_ICON_H / headshotIcon.bh) : 0;
    ctx.textAlign = 'left';
    const nameMax = Math.min(KF_NAME_MAX, W * 0.34);
    const byName = fitText(ctx, k.by, nameMax);
    const victimName = fitText(ctx, k.victim, nameMax);
    const byW = ctx.measureText(byName).width;
    const vW = ctx.measureText(victimName).width;
    const w = 12 + byW + KF_ICON_PAD + iconW + KF_ICON_PAD + vW + (hsW ? KF_ICON_PAD + hsW : 0) + 12;
    const x = W - 24 - w;
    const cy = y + (rowH - 6) / 2;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    roundRect(ctx, x, y, w, rowH - 6, 8);
    ctx.fill();
    let cx = x + 12;
    ctx.fillStyle = TEAM_COLOR[k.byTeam];
    ctx.fillText(byName, cx, cy);
    cx += byW + KF_ICON_PAD;
    if (icon) {
      drawSpriteBox(ctx, icon, cx, cy - KF_ICON_H / 2, iconW, KF_ICON_H);
    }
    cx += iconW + KF_ICON_PAD;
    ctx.fillStyle = TEAM_COLOR[k.victimTeam];
    ctx.fillText(victimName, cx, cy);
    if (hsW) {
      cx += vW + KF_ICON_PAD;
      drawSpriteBox(ctx, headshotIcon, cx, cy - KF_ICON_H / 2, hsW, KF_ICON_H);
    }
    y += rowH;
  }
  ctx.restore();
};
